import { inject, Injectable } from "@angular/core";
import { BasiqActions } from "./basiq.actions";
import { SyncStatusEnum } from "../../../../models/types/general-types";
import { GlossBasiqTokenResponse } from "@bitwarden/web-vault/app/models/types/basiq.types";
import {
  BasiqLocalStorage,
  BasiqRedirectEnum,
} from "@bitwarden/web-vault/app/models/enum/basiq.enum";
import { Verification } from "@bitwarden/common/types/verification";
import { UserVerificationService } from "@bitwarden/common/abstractions/userVerification/userVerification.service.abstraction";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";

@Injectable({
  providedIn: "root",
})
export class BasiqUserAction extends BasiqActions {
  syncStatus: SyncStatusEnum;
  private userVerificationService: UserVerificationService;

  constructor() {
    super();
    this.userVerificationService = inject(UserVerificationService);
  }

  /** Connects a new user to Basiq */
  async connectNewUser() {
    try {
      await this.basiqAuthApi.createUser();
      const userToken = await this.basiqAuthApi.createUserToken();
      this.saveTokenToLocal(BasiqLocalStorage.basiqUserToken, userToken);

      /** redirect to basiq consent screen... from there basiq handles all and get back to us with jobIds if all goes well*/
      window.location.href = `https://consent.basiq.io/home?token=${userToken.token.access_token}`;
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        "redirecting a new basiq user to Basiq Consent Page",
        null,
      );
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  async connectExistingUser(isUserConsent: boolean) {
    try {
      const userToken = await this.getUserToken();
      if (!userToken) {
        return;
      }

      if (isUserConsent) {
        window.location.href = `https://consent.basiq.io/home?&token=${userToken.token.access_token}&action=connect`;
        return;
      }

      window.location.href = `https://consent.basiq.io/home?token=${userToken.token.access_token}`;
    } catch (error) {
      await this.glossSyncService.logError([], error, "Connecting existing user", null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  /** */
  private async getUserToken() {
    try {
      const tokenInLocal = this.getTokenFromLocalStorage(BasiqLocalStorage.basiqUserToken);
      if (tokenInLocal) {
        return tokenInLocal;
      }

      const newToken = await this.basiqAuthApi.createUserToken();
      this.saveTokenToLocal(BasiqLocalStorage.basiqUserToken, newToken);
      return newToken;
    } catch (error) {
      await this.glossSyncService.logError([], error, "Getting user token", null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
      return null;
    }
  }

  private saveTokenToLocal(key: string, basiqTokenObject: GlossBasiqTokenResponse) {
    const value = JSON.stringify(basiqTokenObject);
    const expirationTimeInSeconds = basiqTokenObject.token.expires_in;
    const now = new Date();
    const item = {
      value: value,
      expiry: now.getTime() + expirationTimeInSeconds * 1000, // Convert minutes to milliseconds
    };
    sessionStorage.setItem(key, JSON.stringify(item));
  }

  private getTokenFromLocalStorage(name: string): GlossBasiqTokenResponse {
    const storedValue = JSON.parse(sessionStorage.getItem(name));
    const expiryTime = storedValue ? storedValue.expiry : 0;
    const nowTime = new Date().getTime();
    if (nowTime >= expiryTime) {
      return null;
    } else {
      return JSON.parse(storedValue.value);
    }
  }

  async getActiveConsent() {
    try {
      const glossConsentResponse = await this.basiqAuthApi.consent();
      if (glossConsentResponse && glossConsentResponse.consents.data?.length) {
        return glossConsentResponse.consents.data
          .sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime())
          .find((consent) => consent.status === "active");
      }
      const error = new Error("No consent found");
      await this.glossSyncService.logError(
        [],
        error,
        "basiq consent check",
        SyncStatusEnum.unknownError,
      );
      return null;
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        "basiq consent check",
        SyncStatusEnum.unknownError,
      );
      throw error;
    }
  }

  /**
   * @param keepDataIn boolean
   * @param masterPassword Verification
   * */
  async removeConsent(keepDataIn: boolean, masterPassword: Verification): Promise<boolean> {
    try {
      await this.userVerificationService.verifyUser(masterPassword);
    } catch (e) {
      this.confirmationDialog.notify("error", "error", "invalidMasterPassword");
      return false;
    }

    const userConsent = await this.getActiveConsent();
    if (!userConsent) {
      this.confirmationDialog.notify("error", "error", "noActiveConsentFound");
      return false;
    }

    const isRemoved = await this.basiqAuthApi.removeConsent(userConsent.id);

    if (!isRemoved) {
      this.confirmationDialog.notify("error", "error", "somethingWentWrong");
      return false;
    }

    return true;
  }

  /** Takes user to the specific institution login for Basiq */
  async connectToInstitution(insto: InstitutionView) {
    const userToken = await this.getUserToken();
    if (!userToken) {
      return;
    }

    const connectionResponse = await this.basiqAuthApi.accountConnections(insto.instoBasiqId);
    const institutionConnection = connectionResponse.connections.find(
      (connection) => connection.institution.id === insto.instoBasiqId,
    );
    const params = new URLSearchParams({
      [BasiqRedirectEnum.token]: userToken.token.access_token,
      [BasiqRedirectEnum.connectionId]: institutionConnection.id,
    });

    window.location.href = `https://consent.basiq.io/home?${params.toString()}`;
    return;
  }

  /** Takes user to the consent screen and once consented tot he insto's login page */
  async reconnectToInstitution(insto: InstitutionView) {
    const userToken = await this.getUserToken();
    if (!userToken) {
      return;
    }

    const params = new URLSearchParams({
      [BasiqRedirectEnum.token]: userToken.token.access_token,
      [BasiqRedirectEnum.action]: BasiqRedirectEnum.connect,
      [BasiqRedirectEnum.institutionId]: insto.instoBasiqId,
      [BasiqRedirectEnum.state]: BasiqRedirectEnum.resync,
    });

    window.location.href = `https://consent.basiq.io/home?${params.toString()}`;
  }

  /*    async extendConsent() {
    if (await this.basiqUserValidator.()) {
      if (await this.isUserConsent()) {
        const userToken = await this.getUserToken();
        window.location.href = `https://consent.basiq.io/home?&token=${userToken.token.access_token}&action=extend&state=toImport`;
      } else {
        const confirm = await this.confirmationDialogService.confirmFor(
          ConfirmationEnum.givingFirstConsent,
        );

        if (confirm) {
          const isConnectionOpen = await this.isConnectionOpen();
          if (isConnectionOpen) {
            await this.connectToAnotherInstitution();
          }
        }
      }
    } else {
      await this.recurseIfAuthenticate(this.extendConsent.bind(this));
    }
  }*/

  /*  async updateConsent() {
      if (await this.isAuthenticated()) {
        const userToken = await this.getUserToken();
        const isConnectionOpen = await this.isConnectionOpen();
        if (isConnectionOpen) {
          window.location.href = `https://consent.basiq.io/home?&token=${userToken.token.access_token}&action=update&state=toImport`;
        }
      } else {
        await this.recurseIfAuthenticate(this.updateConsent.bind(this));
      }
    }*/

  /*  async connectToAnotherInstitution() {
      if (await this.isAuthenticated()) {
        const userToken = await this.getUserToken();
        const isConnectionOpen = await this.isConnectionOpen();
        if (isConnectionOpen) {
          window.location.href = `https://consent.basiq.io/home?&token=${userToken.token.access_token}&action=connect`;
        }
      } else {
        await this.recurseIfAuthenticate(this.connectToAnotherInstitution.bind(this));
      }
    }*/

  /*  setUserAwareOfConsent(isUserAware: boolean) {
      if (isUserAware) {
        sessionStorage.setItem("userAwareOfConsent", "true");
      } else {
        sessionStorage.removeItem("userAwareOfConsent");
      }
      return isUserAware;
    }*/

  // /** Ask user for a re-consent if rejected then ask them to either keep or delete their data in gloss as per regulations */
  // async runConsentFlow(askingForReConsent: ConfirmationEnum, removingBasiqData: ConfirmationEnum) {
  //   const confirm = await this.confirmationDialogService.confirmFor(askingForReConsent);
  //   if (confirm) {
  //     await this.redirectToBasiqConsent();
  //   } else {
  //     const confirm = await this.confirmationDialogService.confirmFor(removingBasiqData);
  //     if (confirm) {
  //       await this.removeBasiqData();
  //     }
  //     return this.setUserAwareOfConsent(true);
  //   }
  // }
}
